import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ROUTES } from 'constants.js';
import { WALLET_CURRENCY_SYMBOLS } from 'constants/wallet';
import { EmptyIcon } from 'components/Shared/Icons';
import OffsetPaginationTable from 'components/Shared/OffsetPaginationTable';
import { getCurrencySymbol } from 'utils/currencyUtil';
import useTranslation from 'utils/hooks/useTranslation';
import useAmplitude from 'utils/hooks/useAmplitude';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import { createOrderString } from 'pages/FundingRequests/helper';
import tableColumns from './tableColumns';

import styles from './style.module.scss';

const BalanceHistory = ({
  items = [],
  currency,
  pageNumber = 1,
  totalCount = 0,
  isFetching = false,
  transactionType = [],
  transactionStatus = [],
  orderBy = null,
  onPageChange,
  onPageSizeChange,
  onTransactionTypeChange,
  onTransactionStatusChange,
  onOrderByChange,
}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const handleReportsLinkClick = () =>
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletReportsLinkClicked, {
      linkSource: AMPLITUDE_WALLET_PROPERTY_VALUES.linkSource.balanceHistory,
    });
  const { currentCorporation } = useSelector(state => state.user);
  // Prefer the wallet's own currency; fall back to the corporation country when no wallet is loaded
  const symbol =
    WALLET_CURRENCY_SYMBOLS[String(currency ?? '').toLowerCase()] ??
    getCurrencySymbol(currentCorporation?.country);

  const columns = tableColumns({
    translateText,
    symbol,
    showTypeFilter: Boolean(onTransactionTypeChange),
    transactionTypeFilters: transactionType,
    showStatusFilter: Boolean(onTransactionStatusChange),
    transactionStatusFilters: transactionStatus,
    showAmountSorter: Boolean(onOrderByChange),
    orderBy,
  });

  const hasListChanged = (next, current) =>
    next.length !== current.length || next.some(value => !current.includes(value));

  // antd reports filters and sorter together on every change; only forward what actually moved.
  const handleTableChange = (_pagination, filters, sorter) => {
    const nextTransactionType = filters.transactionType ?? [];
    if (hasListChanged(nextTransactionType, transactionType)) {
      onTransactionTypeChange?.(nextTransactionType);
    }

    const nextTransactionStatus = filters.status ?? [];
    if (hasListChanged(nextTransactionStatus, transactionStatus)) {
      onTransactionStatusChange?.(nextTransactionStatus);
    }

    const nextOrderBy = createOrderString(sorter?.field, sorter?.order);
    if (nextOrderBy !== orderBy) {
      onOrderByChange?.(nextOrderBy);
    }
  };

  return (
    <div className={styles.history} data-testid="walletBalanceHistory">
      <h3 className={styles.historyTitle}>
        {translateText('corporateAccounts.wallet.dashboard.history.title')}
      </h3>
      <p className={styles.historyDescription}>
        {translateText('corporateAccounts.wallet.dashboard.history.description')}{' '}
        <Link
          to={ROUTES.REPORTS}
          className={styles.link}
          data-testid="walletBalanceHistoryReportsLink"
          onClick={handleReportsLinkClick}
        >
          {translateText('corporateAccounts.reports.main.title')}
        </Link>
        .
      </p>
      <div
        className={classNames(styles.historyRows, { [styles.historyRowsFetching]: isFetching })}
        data-testid="walletBalanceHistoryRows"
      >
        <OffsetPaginationTable
          rowKey="id"
          columns={columns}
          current={pageNumber}
          totalCount={totalCount}
          dataSource={items}
          paginationTestId="walletBalanceHistoryPagination"
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onChange={handleTableChange}
          onRow={() => ({ 'data-testid': 'walletBalanceHistoryRow' })}
          locale={{
            emptyText: (
              <div className={styles.historyEmpty} data-testid="walletBalanceHistoryEmpty">
                <div className={styles.historyEmptyIcon}>
                  <EmptyIcon />
                </div>
                <div className={styles.historyEmptyCopy}>
                  {translateText('corporateAccounts.wallet.dashboard.history.emptyCopy')}
                </div>
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default BalanceHistory;
